import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Filler, Tooltip, Legend,
} from 'chart.js';
import ChartCard from '../components/chart/ChartCard';
import { setOptions, chartConfig, setOptionsStackedPercent } from '../utils/chartConfigs';
import { colors } from '../utils/colors';
import { http } from '../services/http';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const STACKED = true;
const FILL = true;
const CUSTOMHEIGHT = true;

const clientColumnPrefix = {
  studio: 'studio',
  songwriter: 'songwriter',
};

function formatWeek(ts) {
  const date = new Date(parseInt(ts, 10) * 1000);
  return date.toISOString().slice(0, 10);
}

const UserSegmentsByWeek = ({ client = 'studio' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const clientPrefix = clientColumnPrefix[client] || 'studio';
  const clientTitle = clientPrefix === 'songwriter' ? 'Songwriter' : 'Studio';

  useEffect(() => {
    http.get('/admin/stats/view/materialized_user_segments_per_registration_week')
      .then(res => {
        const processed = res.data.map(row => {
          const paid = parseInt(row[`paid_ever_users_${clientPrefix}`], 10) || 0;
          const multi = parseInt(row[`multiple_songs_users_${clientPrefix}`], 10) || 0;
          const finished = parseInt(row[`finished_users_${clientPrefix}`], 10) || 0;
          const started = parseInt(row[`started_users_${clientPrefix}`], 10) || 0;
          const remaining = parseInt(row[`remaining_users_${clientPrefix}`], 10) || 0;
          const total = paid + multi + finished + started + remaining;
          const pct = (num, denom = total) => denom > 0 ? ((num / denom) * 100).toFixed(2) : '0.00';
          return {
            ...row,
            paid_ever_pct: pct(paid),
            multiple_songs_pct: pct(multi),
            finished_pct: pct(finished),
            started_pct: pct(started),
            remaining_pct: pct(remaining),
            didAnything_pct: pct(paid + multi + finished + started, total),
            finishedOfStarted_pct: pct(paid + multi + finished, paid + multi + finished + started),
            paidOfDidAnything_pct: pct(paid, paid + multi + finished + started),
          };
        });
        setData(processed);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Error fetching data');
        setLoading(false);
      });
  }, [clientPrefix]);

  const weekLabels = data.map(row => row.registration_week ? formatWeek(row.registration_week) : '');
  const segmentLabels = ['Paid', 'Multiple Songs', 'Finished', 'Started', 'Remaining'];
  const segmentColors = [colors.secondary, colors.primary, colors.yellow, colors.teal, colors.lightred];
  const segmentValues = [
    data.map(row => Number(row[`paid_ever_users_${clientPrefix}`]) || 0),
    data.map(row => Number(row[`multiple_songs_users_${clientPrefix}`]) || 0),
    data.map(row => Number(row[`finished_users_${clientPrefix}`]) || 0),
    data.map(row => Number(row[`started_users_${clientPrefix}`]) || 0),
    data.map(row => Number(row[`remaining_users_${clientPrefix}`]) || 0),
  ];
  const segmentPctValues = [
    data.map(row => Number(row.paid_ever_pct)),
    data.map(row => Number(row.multiple_songs_pct)),
    data.map(row => Number(row.finished_pct)),
    data.map(row => Number(row.started_pct)),
    data.map(row => Number(row.remaining_pct)),
  ];

  const renderContent = (chartData, options) => (
    <>
      {loading && <p>Loading...</p>}
      {error && <p style={{ color: '#d32f2f' }}>{error}</p>}
      {!loading && !error && data.length > 0 && <Line data={chartData} options={options} />}
    </>
  );

  return (
    <section>
      <div className="chart-grid">
        <div className="chart-col">
          <ChartCard header={`Users with any event in ${clientTitle} (Stacked)`} description="Stacked absolute user counts by segment." height="350px">
            {renderContent(
              chartConfig(weekLabels, segmentLabels, segmentValues, segmentColors, FILL, STACKED),
              setOptions(STACKED, undefined, CUSTOMHEIGHT)
            )}
          </ChartCard>
        </div>
        <div className="chart-col">
          <ChartCard header="User Segment Percentages (Stacked)" description="Stacked percentages by segment." height="350px">
            {renderContent(
              chartConfig(weekLabels, segmentLabels.map(l => `${l} (%)`), segmentPctValues, segmentColors, FILL, STACKED),
              setOptionsStackedPercent(true, true, true)
            )}
          </ChartCard>
        </div>

        <div className="chart-col-full">
          <ChartCard header="User Segments, not stacked"
            description="Of all users with account and events that week, what segment they ended up in." height="350px">
            {renderContent(
              chartConfig(weekLabels, ['Paid', 'Multiple Songs', 'Finished', 'Started'],
                segmentValues.slice(0, 4), segmentColors.slice(0, 4)),
              setOptions(undefined, undefined, true)
            )}
          </ChartCard>
        </div>

        <div className="chart-col-full">
          <ChartCard header="Users that did anything out of users with account, and who paid"
            description="Of all users with account and events that week, how many did anything and how many paid." height="350px">
            {renderContent(
              chartConfig(weekLabels, ['Paid Ever (%)', 'Did Anything (%)'],
                [data.map(row => Number(row.paid_ever_pct)), data.map(row => Number(row.didAnything_pct))],
                [colors.secondary, colors.primary]),
              setOptions(undefined, undefined, true)
            )}
          </ChartCard>
        </div>

        <div className="chart-col-full">
          <ChartCard header="Of those who did anything, who finished and who paid"
            description="Paid lags behind because of the 10 day trial." height="350px">
            {renderContent(
              chartConfig(weekLabels, ['Finished of Started (%)', 'Paid of Did Anything (%)'],
                [data.map(row => Number(row.finishedOfStarted_pct)), data.map(row => Number(row.paidOfDidAnything_pct))],
                [colors.yellow, colors.secondary]),
              setOptions(undefined, undefined, true)
            )}
          </ChartCard>
        </div>
      </div>
    </section>
  );
};

export default UserSegmentsByWeek;
